import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Quote, Star, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const testimonials = [
  {
    id: 1,
    name: "Lakshmi Kishore",
    location: "Kochi, Kerala",
    message: "I've been using Agniya Spices chilli powder for over a year now. The color and aroma it brings to my sambar and fish curry is unmatched. My whole family loves the authentic taste!",
    image: "https://images.unsplash.com/photo-1759840278381-bf7d5e332050?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjB3b21hbiUyMHNtaWxpbmclMjBwb3J0cmFpdHxlbnwwfHx8fDE3Njk2ODYxMjB8MA&ixlib=rb-4.1.0&q=85",
    rating: 5,
    category: "home-cooking",
    headline: "Perfect for daily cooking"
  },
  {
    id: 2,
    name: "Chef Sanjay Ramankulath",
    location: "Thrissur, Kerala",
    message: "As a restaurant Chef, consistency in spices is crucial. Agniya Spices delivers the same quality every single time. My customers always compliment the rich color of our dishes.",
    image: "https://images.pexels.com/photos/30660319/pexels-photo-30660319.jpeg",
    rating: 5,
    category: "bulk-hotel",
    headline: "Consistent quality for restaurants"
  },
  {
    id: 3,
    name: "Gayathri",
    location: "Thrissur, Kerala",
    message: "What I love most about Agniya is that there's no artificial color. I can cook for my children knowing the spices are pure and safe. The turmeric powder is so fresh and aromatic!",
    image: "https://images.unsplash.com/photo-1759840278511-f73a3d62fb9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjB3b21hbiUyMHNtaWxpbmclMjBwb3J0cmFpdHxlbnwwfHx8fDE3Njk2ODYxMjB8MA&ixlib=rb-4.1.0&q=85",
    rating: 5,
    category: "quality-freshness",
    headline: "Pure and safe for family"
  },
  {
    id: 4,
    name: "Sibin E Sidharthan",
    location: "Thrissur, Kerala",
    message: "In our daily life we prefer quality spices. Agniya Spices has been our trusted partner for daily use & for all major events. The coriander powder adds perfect flavor to our biryanis!",
    image: "https://images.unsplash.com/photo-1664101606938-e664f5852fac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGluZGlhbiUyMGN1c3RvbWVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzY5Njg2MTEwfDA&ixlib=rb-4.1.0&q=85",
    rating: 5,
    category: "biriyani-gravies",
    headline: "Perfect for biriyani"
  },
  {
    id: 5,
    name: "Nikhil Dev",
    location: "Thrissur, Kerala",
    message: "I discovered Agniya Spices through a friend's recommendation, and I haven't looked back since. The fine grind of their chilli powder mixes so well in all my dishes. Truly pure and trusted!",
    image: "https://images.unsplash.com/photo-1580746453801-37b0bc56f3b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB3b21hbiUyMHNtaWxpbmclMjBwb3J0cmFpdHxlbnwwfHx8fDE3Njk2ODYxMjB8MA&ixlib=rb-4.1.0&q=85",
    rating: 5,
    category: "home-cooking",
    headline: "Fine grind that mixes perfectly"
  },
  {
    id: 6,
    name: "Jithin Venugopalan Nambiar",
    location: "Kannur, Kerala",
    message: "The quality of Agniya Spices is exceptional. I use their products daily in my home kitchen, and the freshness of the spices makes a noticeable difference in every meal. Highly recommended!",
    image: "https://images.unsplash.com/photo-1595956481935-a9e254951d49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGluZGlhbiUyMGN1c3RvbWVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzY5Njg2MTEwfDA&ixlib=rb-4.1.0&q=85",
    rating: 5,
    category: "quality-freshness",
    headline: "Exceptional freshness"
  }
];

const categories = [
  { id: 'all', label: 'All Reviews', icon: '⭐' },
  { id: 'home-cooking', label: 'Home Cooking', icon: '🏠' },
  { id: 'biriyani-gravies', label: 'Biriyani & Gravies', icon: '🍛' },
  { id: 'bulk-hotel', label: 'Bulk / Hotel Use', icon: '🏪' },
  { id: 'quality-freshness', label: 'Quality & Freshness', icon: '✨' }
];

const TestimonialsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredTestimonials = activeCategory === 'all' 
    ? testimonials 
    : testimonials.filter(t => t.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Customer Testimonials - Agniya Spices | What Our Customers Say</title>
        <meta 
          name="description" 
          content="Read what our happy customers say about Agniya Spices. Trusted by households, restaurants, and caterers across Kerala for premium quality Indian spices." 
        />
        <meta 
          name="keywords" 
          content="Agniya Spices reviews, customer testimonials, spice reviews Kerala, Indian spices feedback, chilli powder reviews" 
        />
        <link rel="canonical" href="https://agniyaspices.com/testimonials" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Customer Testimonials - Agniya Spices | What Our Customers Say" />
        <meta property="og:description" content="Read what our happy customers say about Agniya Spices. Trusted by households, restaurants, and caterers across Kerala." />
        <meta property="og:image" content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp" />
        <meta property="og:url" content="https://agniyaspices.com/testimonials" />
        <meta property="og:site_name" content="Agniya Spices" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Customer Testimonials - Agniya Spices" />
        <meta name="twitter:description" content="Read what our happy customers say about Agniya Spices." />
        <meta name="twitter:image" content="https://storage.googleapis.com/agniya-images/website/images/Red%20Chilli%20Powder.webp" />

        {/* WebPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Customer Testimonials - Agniya Spices",
            "url": "https://agniyaspices.com/testimonials",
            "description": "Read what our customers say about Agniya Spices. Trusted by households, restaurants, and caterers across Kerala.",
            "publisher": {
              "@type": "Organization",
              "name": "Agniya Spices"
            }
          })}
        </script>

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://agniyaspices.com" },
              { "@type": "ListItem", "position": 2, "name": "Testimonials", "item": "https://agniyaspices.com/testimonials" }
            ]
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/favicon.ico"
              alt="Agniya Spices logo"
              className="h-10 w-10 rounded-sm object-contain"
            />
            <div className="text-2xl font-bold text-[#8B2820]">
              AGNIYA SPICES
            </div>
          </Link>
          <nav className="hidden md:flex space-x-8">
            <Link to="/" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Home</Link>
            <Link to="/#about" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">About Us</Link>
            
            {/* Products Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-[#4a3a33] hover:text-[#8B2820] transition-colors focus:outline-none">
                Products
                <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-[#8B2820]/20 shadow-lg">
                <DropdownMenuItem asChild>
                  <Link to="/products/chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Red Chilli Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/coriander-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Coriander Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/turmeric-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Turmeric Powder
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/products/kashmiri-chilli-powder" className="cursor-pointer px-4 py-2 hover:bg-[#8B2820]/10 text-[#4a3a33]">
                    Kashmiri Chilli Powder
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Link to="/spices-guide" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Spice Guide</Link>
            <Link to="/testimonials" className="text-[#8B2820] font-semibold transition-colors">Testimonials</Link>
            <Link to="/faq" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">FAQ</Link>
            <Link to="/#quality" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Quality</Link>
            <Link to="/contact" className="text-[#4a3a33] hover:text-[#8B2820] transition-colors">Contact Us</Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#8B2820] to-[#5d1a15]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center mb-6">
            <Link to="/">
              <Button variant="ghost" className="text-white hover:bg-white/20">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Customer Stories
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Hear from families, chefs, and businesses who trust Agniya Spices for their culinary journey
            </p>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-8 bg-white sticky top-[72px] z-40 shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                variant={activeCategory === category.id ? 'default' : 'outline'}
                className={activeCategory === category.id 
                  ? 'bg-[#8B2820] text-white hover:bg-[#6d1f19]' 
                  : 'border-[#8B2820] text-[#8B2820] hover:bg-[#8B2820]/10'}
              >
                <span className="mr-2">{category.icon}</span>
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {filteredTestimonials.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-[#736c64]">No testimonials found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTestimonials.map((testimonial, index) => (
                <Card 
                  key={testimonial.id}
                  className="border-none shadow-lg hover:shadow-2xl transition-all duration-500 bg-white overflow-hidden group"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`testimonial-card-${testimonial.id}`}
                >
                  <CardContent className="p-0">
                    {/* Quote Icon */}
                    <div className="relative p-8 pb-4">
                      <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Quote className="h-16 w-16 text-[#8B2820]" />
                      </div>
                      
                      {/* Customer Photo & Info */}
                      <div className="flex items-center mb-4">
                        <div className="relative">
                          <img 
                            src={testimonial.image} 
                            alt={testimonial.name}
                            className="w-20 h-20 rounded-full object-cover border-4 border-[#8B2820]/20 group-hover:border-[#8B2820]/40 transition-colors"
                          />
                          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#8B2820] rounded-full flex items-center justify-center">
                            <Quote className="h-3 w-3 text-white" />
                          </div>
                        </div>
                        <div className="ml-4 flex-1">
                          <h3 className="text-xl font-bold text-[#4a3a33]">{testimonial.name}</h3>
                          <div className="flex items-center text-[#736c64] mt-1">
                            <MapPin className="h-4 w-4 mr-1" />
                            <span className="text-sm">{testimonial.location}</span>
                          </div>
                        </div>
                      </div>

                      {/* Star Rating */}
                      <div className="flex items-center mb-3">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-[#8B2820] text-[#8B2820]" />
                        ))}
                      </div>

                      {/* Headline */}
                      {testimonial.headline && (
                        <h4 className="text-lg font-semibold text-[#8B2820] mb-3">
                          "{testimonial.headline}"
                        </h4>
                      )}

                      {/* Testimonial Message */}
                      <p className="text-[#4a3a33] leading-relaxed italic">
                        "{testimonial.message}"
                      </p>
                    </div>

                    {/* Bottom Accent */}
                    <div className="h-2 bg-gradient-to-r from-[#8B2820] to-[#c94a3f] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-[#8B2820] mb-6">
            Try Agniya Spices Today
          </h2>
          <p className="text-xl text-[#4a3a33] mb-8 max-w-2xl mx-auto">
            Join our happy customers and experience the pure taste of authentic Indian spices. 
            From home kitchens to professional restaurants, Agniya Spices delivers quality you can trust.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/testimonials/review">
              <Button 
                size="lg"
                className="bg-[#8B2820] hover:bg-[#6d1f19] text-white px-12 py-6 text-lg rounded-md shadow-lg transition-all duration-300 hover:scale-105"
                data-testid="review-submit-cta-button"
              >
                Share Your Experience
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg"
                variant="outline"
                className="border-[#8B2820] text-[#8B2820] hover:bg-[#8B2820]/10 px-12 py-6 text-lg rounded-md"
                data-testid="contact-cta-button"
              >
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestimonialsPage;
